import React, { useEffect, useMemo, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Nav } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import {
  canViewPendingOnMe,
  canViewReports,
  isAgentRole,
  getStoredUserType,
  getWorkflowHistoryLabel,
  getWorkflowQueueLabel,
  isDepartmentUserRole,
  isWorkflowOnlyRole,
} from "../../utils/roleAccess";

const isActive = (pathname, item) => {
  if (item.match) return item.match(pathname);
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
};

const REPORTS_SECTION = {
  title: "Reports",
  items: [
    {
      key: "pendencyReport",
      to: "/pendency?type=STATE&code=0&category=all",
      icon: "fa fa-bar-chart",
      label: "Daily Summary",
      match: (p) => p === "/pendency",
    },
    {
      key: "pendencyCatReport",
      to: "/pendency-category?type=STATE&code=0&category=New&date=",
      icon: "fa fa-pie-chart",
      label: "Category Summary",
      match: (p) => p === "/pendency-category",
    },
  ],
};

function NavbarMenu() {
  const location = useLocation();
  const [userType, setUserType] = useState(() => getStoredUserType());

  useEffect(() => {
    setUserType(getStoredUserType());
  }, [location.pathname]);

  const sections = useMemo(() => {
    if (isDepartmentUserRole(userType)) {
      return [
        {
          title: "Overview",
          items: [
            {
              key: "dashboard",
              to: "/",
              icon: "fa fa-desktop",
              label: "Dashboard",
              match: (p) => p === "/",
            },
          ],
        },
        {
          title: "My Work",
          items: [
            {
              key: "workflowQueue",
              to: "/workflow-queue",
              icon: "fa fa-tasks",
              label: "My Actions",
              match: (p) => p === "/workflow-queue",
            },
          ],
        },
      ];
    }

    if (isWorkflowOnlyRole(userType)) {
      return [
        {
          title: "Overview",
          items: [
            {
              key: "dashboard",
              to: "/",
              icon: "fa fa-desktop",
              label: "Dashboard",
              match: (p) => p === "/",
            },
          ],
        },
        {
          title: "My Work",
          items: [
            {
              key: "workflowQueue",
              to: "/workflow-queue",
              icon: "fa fa-tasks",
              label: getWorkflowQueueLabel(userType),
              match: (p) => p === "/workflow-queue",
            },
            {
              key: "workflowHistory",
              to: "/workflow-history",
              icon: "fa fa-history",
              label: getWorkflowHistoryLabel(userType),
              match: (p) => p === "/workflow-history",
            },
          ],
        },
        ...(canViewReports(userType) ? [REPORTS_SECTION] : []),
      ];
    }

    const items = [
      {
        title: "Overview",
        items: [
          {
            key: "dashboard",
            to: "/",
            icon: "fa fa-desktop",
            label: "Dashboard",
            match: (p) => p === "/",
          },
        ],
      },
      {
        title: "Complaints",
        items: [
          {
            key: "addComplaint",
            to: "/add-complain",
            icon: "fa fa-plus",
            label: "Add Complaint",
          },
          {
            key: "viewComplain",
            to: "/view-complain",
            icon: "fa fa-id-card-o",
            label: "View Complaint",
          },
          ...(canViewPendingOnMe(userType)
            ? [
                {
                  key: "pendingOnMe",
                  to: "/pending-on-me",
                  icon: "fa fa-clock-o",
                  label: "Pending on Me",
                  match: (p) => p === "/pending-on-me",
                },
              ]
            : []),
        ],
      },
      ...(canViewReports(userType) ? [REPORTS_SECTION] : []),
    ];

    if (isAgentRole(userType)) {
      items.splice(2, 0, {
        title: "Workflow",
        items: [
          {
            key: "workflowQueue",
            to: "/workflow-queue",
            icon: "fa fa-tasks",
            label: "Follow-up Queue",
            match: (p) => p === "/workflow-queue",
          },
        ],
      });
    }

    if (userType === "ADMIN") {
      items.splice(2, 0, {
        title: "Workflow",
        items: [
          {
            key: "orgStructure",
            to: "/org-structure",
            icon: "fa fa-object-group",
            label: "Org Structure",
            match: (p) => p === "/org-structure",
          },
          {
            key: "complaintCategory",
            to: "/complaint-category",
            icon: "fa fa-tags",
            label: "Complaint Categories",
            match: (p) => p === "/complaint-category",
          },
          {
            key: "workflowOfficerStatus",
            to: "/workflow-officer-status",
            icon: "fa fa-user-circle-o",
            label: "Officer Status",
            match: (p) => p === "/workflow-officer-status",
          },
          {
            key: "workflowSettings",
            to: "/workflow-settings",
            icon: "fa fa-sliders",
            label: "Workflow Settings",
            match: (p) => p === "/workflow-settings",
          },
        ],
      });

      items.push({
        title: "Users",
        items: [
          {
            key: "userList",
            to: "/user-list",
            icon: "fa fa-users",
            label: "User List",
          },
          {
            key: "createAccount",
            to: "/create-account",
            icon: "fa fa-user-plus",
            label: "Create Account",
          },
        ],
      });
    }

    return items;
  }, [userType]);

  const pathname = location.pathname;

  return (
    <div>
      <div className="tab-content p-l-0 p-r-0">
        <div className="active" id="menu">
          <Nav id="left-sidebar-nav" className="sidebar-nav">
            <ul id="main-menu" className="metismenu">
              {sections.map((section) => (
                <li key={section.title} className="ccc-sidebar-section">
                  <p className="ccc-sidebar-section__title">{section.title}</p>
                  <ul>
                    {section.items.map((item) => (
                      <li
                        key={item.key}
                        className={
                          isActive(pathname, item) ? "activeClass" : ""
                        }
                      >
                        <Link to={item.to}>
                          <i className={item.icon} aria-hidden="true" />
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </Nav>
        </div>
      </div>
    </div>
  );
}

export default NavbarMenu;
